// Lead Generation System — the in-page Prelead detail view (a panel WITHIN the
// Preleads tab; NO portal route). Shows the four-track action bar, inline-
// editable extracted fields, the raw pasted text, the notes thread (with an
// Add-note box AND a "Paste update" re-paste box), captured links + listing
// URL + attached NDA/CIM files, and the activity timeline.
//
// Phoenix Capital is the Loan Administrator — the "Listing broker (source)"
// fields are the third-party source of the business-for-sale listing.

import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, CheckCircle2, ExternalLink, FileText, Link2, Sparkles } from "lucide-react";
import {
  Button,
  Card,
  Dropzone,
  EmptyState,
  FormField,
  Input,
  Skeleton,
  StatusBadge,
  type StatusTone,
  Textarea,
  Timestamp,
  UploadFileRow,
  type UploadFileState,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { formatCurrency } from "@/design-system/lib/format";
import { useWidgetData, type WidgetData } from "../../dashboard/engine";
import {
  leadGeneratorApi,
  type CapturedLink,
  type PreleadActivityDto,
  type PreleadAttachmentDto,
  type PreleadDetailDto,
  type PreleadEarningsDto,
  type PreleadFieldDiscrepancyDto,
  type PreleadNoteDto,
  type UpdatePreleadRequest,
} from "../../api/leadGenerator";
import { PreleadActionButtons } from "./PreleadActionButtons";
import { confirmedMilestones, preleadDisplayName } from "./preleadStatus";
import { STATUS_LABEL, STATUS_TONE } from "./pipeline";

const MONEY = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;
const MAX_FILE_BYTES = 25 * 1024 * 1024;

/** Compact human file size — no shared formatter exists in @/design-system/lib/format. */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface EditForm {
  businessName: string;
  industry: string;
  contactFirstName: string;
  contactLastName: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  askingPrice: string;
  cashFlow: string;
  listingUrl: string;
  brokerName: string;
  brokerCompany: string;
}

function toForm(d: PreleadDetailDto): EditForm {
  return {
    businessName: d.businessName ?? "",
    industry: d.industry ?? "",
    contactFirstName: d.contactFirstName ?? "",
    contactLastName: d.contactLastName ?? "",
    email: d.email ?? "",
    phone: d.phone ?? "",
    city: d.city ?? "",
    state: d.state ?? "",
    askingPrice: d.askingPrice != null ? String(d.askingPrice) : "",
    cashFlow: d.cashFlow != null ? String(d.cashFlow) : "",
    listingUrl: d.listingUrl ?? "",
    brokerName: d.brokerName ?? "",
    brokerCompany: d.brokerCompany ?? "",
  };
}

function toUpdate(f: EditForm): UpdatePreleadRequest {
  const num = (s: string) => (s.trim() === "" ? null : Number(s));
  return {
    businessName: f.businessName,
    industry: f.industry,
    contactFirstName: f.contactFirstName,
    contactLastName: f.contactLastName,
    email: f.email,
    phone: f.phone,
    city: f.city,
    state: f.state,
    askingPrice: num(f.askingPrice),
    cashFlow: num(f.cashFlow),
    listingUrl: f.listingUrl,
    brokerName: f.brokerName,
    brokerCompany: f.brokerCompany,
  };
}

function parseLinks(json: string | null): CapturedLink[] {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return [];
    return arr.filter(
      (l): l is CapturedLink => typeof l?.href === "string" && /^https?:\/\//i.test(l.href),
    );
  } catch {
    return [];
  }
}

export function PreleadDetailPanel({
  preleadId,
  onBack,
  onUpdated,
  onRack,
}: {
  preleadId: string;
  onBack: () => void;
  /** Bubble a fresh detail so the list row stays in sync. */
  onUpdated: (detail: PreleadDetailDto) => void;
  onRack?: () => void;
}) {
  const fetched = useWidgetData(async () => leadGeneratorApi.getPrelead(preleadId), [preleadId]);
  const notes = useWidgetData(async () => leadGeneratorApi.listNotes(preleadId), [preleadId]);
  const activity = useWidgetData(async () => leadGeneratorApi.getActivity(preleadId), [preleadId]);
  const earnings = useWidgetData(async () => leadGeneratorApi.preleadEarnings(preleadId), [preleadId]);

  const [detail, setDetail] = useState<PreleadDetailDto | null>(null);
  const [form, setForm] = useState<EditForm | null>(null);
  useEffect(() => {
    if (fetched.data) {
      setDetail(fetched.data);
      setForm(toForm(fetched.data));
    }
  }, [fetched.data]);

  const applyDetail = (d: PreleadDetailDto) => {
    setDetail(d);
    setForm(toForm(d));
    onUpdated(d);
    activity.reload();
    // A paying milestone advance/revert changes the credited breakdown.
    earnings.reload();
  };

  if (fetched.status === "loading" && !detail) {
    return (
      <Card className="space-y-space-3">
        <BackButton onBack={onBack} />
        <Skeleton className="h-64 w-full" />
      </Card>
    );
  }
  if (!detail || !form) {
    return (
      <Card className="space-y-space-3">
        <BackButton onBack={onBack} />
        <p className="text-small text-status-error m-0" role="alert">
          {fetched.error ?? "Prelead not found."}
        </p>
      </Card>
    );
  }

  const displayName = preleadDisplayName({
    businessName: detail.businessName,
    contactFirstName: detail.contactFirstName,
    contactLastName: detail.contactLastName,
    rawText: detail.rawPastedText,
    listingUrl: detail.listingUrl,
    createdAt: detail.createdAt,
  });
  const links = parseLinks(detail.capturedLinksJson);

  return (
    <div className="space-y-space-4" data-testid={`leadgen-detail-${detail.id}`}>
      <Card className="space-y-space-3">
        <div className="flex flex-wrap items-center justify-between gap-space-3">
          <div className="flex items-center gap-space-3 min-w-0">
            <BackButton onBack={onBack} />
            <div className="min-w-0">
              <h2 className="text-h4 font-display font-bold text-onyx m-0 truncate">
                {displayName}
              </h2>
              <div className="flex flex-wrap items-center gap-space-2">
                <StatusBadge tone={STATUS_TONE[detail.status]}>
                  {STATUS_LABEL[detail.status]}
                </StatusBadge>
                {confirmedMilestones(detail).map((c) => (
                  <StatusBadge
                    key={c.key}
                    tone="ok"
                    data-testid={`leadgen-detail-confirmed-${c.key}`}
                  >
                    <CheckCircle2 size={12} aria-hidden /> {c.label}
                  </StatusBadge>
                ))}
              </div>
            </div>
          </div>
        </div>

        <PreleadActionButtons
          prelead={detail}
          size="sm"
          enableFileDrops
          onUpdated={applyDetail}
          onEscalated={() => fetched.reload()}
          onRack={onRack}
          onUploaded={() => fetched.reload()}
        />
      </Card>

      <EditableFields
        preleadId={detail.id}
        form={form}
        onChange={(patch) => setForm((f) => (f ? { ...f, ...patch } : f))}
        seeded={toForm(detail)}
        onSaved={applyDetail}
      />

      <SourceCard detail={detail} links={links} />

      <EarningsCard earnings={earnings} />

      {detail.rawPastedText && (
        <Card>
          <details>
            <summary className="cursor-pointer text-small font-semibold text-onyx">
              Raw pasted text
            </summary>
            <pre className="mt-space-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-border-light bg-white-warm p-space-3 text-caption text-onyx">
              {detail.rawPastedText}
            </pre>
          </details>
        </Card>
      )}

      <NotesCard
        preleadId={detail.id}
        notes={notes}
        onDetailMerged={applyDetail}
      />

      <ActivityCard activity={activity} />
    </div>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <Button variant="text" size="sm" onClick={onBack} data-testid="leadgen-detail-back">
      <ArrowLeft size={14} aria-hidden /> Back to list
    </Button>
  );
}

// ── Editable fields ──────────────────────────────────────────────────────────

function EditableFields({
  preleadId,
  form,
  onChange,
  seeded,
  onSaved,
}: {
  preleadId: string;
  form: EditForm;
  onChange: (patch: Partial<EditForm>) => void;
  seeded: EditForm;
  onSaved: (detail: PreleadDetailDto) => void;
}) {
  const dirty = useMemo(
    () => (Object.keys(form) as (keyof EditForm)[]).some((k) => form[k] !== seeded[k]),
    [form, seeded],
  );

  const save = useSubmittingMutation(
    () => leadGeneratorApi.update(preleadId, toUpdate(form)),
    { onSuccess: (detail) => onSaved(detail) },
  );

  const field = (label: string, key: keyof EditForm, extra?: { type?: string; placeholder?: string }) => (
    <FormField label={label}>
      <Input
        type={extra?.type ?? "text"}
        inputMode={extra?.type === "number" ? "decimal" : undefined}
        value={form[key]}
        placeholder={extra?.placeholder}
        onChange={(e) => onChange({ [key]: e.target.value } as Partial<EditForm>)}
      />
    </FormField>
  );

  return (
    <Card className="space-y-space-3">
      <div className="flex items-center justify-between gap-space-3">
        <h3 className="font-display text-label uppercase tracking-label text-gray-mid m-0">
          Extracted details
        </h3>
        <Button
          variant="primary"
          size="sm"
          onClick={() => void save.submit()}
          loading={save.pending}
          disabled={!dirty}
          data-testid={`leadgen-detail-save-${preleadId}`}
        >
          Save
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-space-3 sm:grid-cols-2 lg:grid-cols-3">
        {field("Business name", "businessName")}
        {field("Industry", "industry")}
        {field("Contact first name", "contactFirstName")}
        {field("Contact last name", "contactLastName")}
        {field("Email", "email", { type: "email" })}
        {field("Phone", "phone")}
        {field("City", "city")}
        {field("State", "state", { placeholder: "AZ" })}
        {field("Asking price", "askingPrice", { type: "number", placeholder: "450000" })}
        {field("Cash flow", "cashFlow", { type: "number", placeholder: "120000" })}
        {field("Listing URL", "listingUrl")}
        {field("Listing broker (source)", "brokerName")}
        {field("Listing broker company", "brokerCompany")}
      </div>
      {save.error != null && (
        <p className="text-caption text-status-error m-0" role="alert">
          {save.error instanceof Error ? save.error.message : "Couldn't save. Try again."}
        </p>
      )}
    </Card>
  );
}

// ── Source / links / attachments ─────────────────────────────────────────────

function SourceCard({ detail, links }: { detail: PreleadDetailDto; links: CapturedLink[] }) {
  const files = useWidgetData(async () => leadGeneratorApi.listAttachments(detail.id), [detail.id]);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const openFile = async (docId: string) => {
    try {
      const res = await leadGeneratorApi.attachmentDownload(detail.id, docId);
      window.open(res.sasUrl, "_blank", "noopener,noreferrer");
      setDownloadError(null);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Couldn't open that file.");
    }
  };

  return (
    <Card className="space-y-space-3">
      <h3 className="font-display text-label uppercase tracking-label text-gray-mid m-0">
        Links &amp; files
      </h3>
      {detail.listingUrl && (
        <a
          href={detail.listingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-space-1 text-small text-gold-shadow hover:underline break-all"
        >
          {detail.listingUrl} <ExternalLink size={12} aria-hidden />
        </a>
      )}
      {links.length > 0 && (
        <ul className="flex flex-wrap gap-space-2 list-none p-0 m-0">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                title={l.href}
                className="inline-flex max-w-full items-center gap-space-1 rounded-pill border border-border-light bg-white-warm px-space-2 py-[2px] text-caption text-onyx hover:border-gold"
              >
                <Link2 size={12} className="shrink-0 text-gold-shadow" aria-hidden />
                <span className="truncate">{l.label || l.href}</span>
              </a>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-space-2">
        <p className="text-caption uppercase tracking-wide text-gray-mid m-0">Files</p>
        {files.status === "loading" && !files.data ? (
          <Skeleton className="h-12 w-full" />
        ) : files.status === "error" && !files.data ? (
          <p className="text-small text-status-error m-0" role="alert">
            {files.error ?? "Couldn't load files."}
          </p>
        ) : !files.data || files.data.length === 0 ? (
          <p className="text-small text-gray-mid m-0">No files attached yet.</p>
        ) : (
          <ul className="flex flex-col gap-space-2 list-none p-0 m-0">
            {files.data.map((f) => (
              <FileRow key={f.docId} file={f} onDownload={() => void openFile(f.docId)} />
            ))}
          </ul>
        )}
        {downloadError && (
          <p className="text-caption text-status-error m-0" role="alert">
            {downloadError}
          </p>
        )}
      </div>

      <AttachmentUploader preleadId={detail.id} onUploaded={() => files.reload()} />
    </Card>
  );
}

function FileRow({ file, onDownload }: { file: PreleadAttachmentDto; onDownload: () => void }) {
  return (
    <li
      className="flex items-center justify-between gap-space-3 rounded-md border border-border-light bg-white-warm p-space-2"
      data-testid={`leadgen-file-${file.docId}`}
    >
      <div className="flex min-w-0 items-center gap-space-2">
        <FileText size={14} className="shrink-0 text-gold-shadow" aria-hidden />
        <span className="text-small text-onyx truncate" title={file.fileName}>
          {file.fileName}
        </span>
        <span className="shrink-0 text-caption text-gray-mid tabular-nums">
          {formatFileSize(file.sizeBytes)}
        </span>
      </div>
      <Button
        variant="text"
        size="sm"
        onClick={onDownload}
        data-testid={`leadgen-file-download-${file.docId}`}
      >
        Download
      </Button>
    </li>
  );
}

/** Per-file upload state for the general drag-and-drop attachment uploader. */
interface UploadEntry {
  id: string;
  name: string;
  sizeBytes: number;
  state: UploadFileState;
  message: string | null;
}

// General drag-and-drop uploader (NOT gated behind any NDA/CIM track) — accepts
// MULTIPLE files, one request per file, and reloads the file list when all done.
function AttachmentUploader({
  preleadId,
  onUploaded,
}: {
  preleadId: string;
  onUploaded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<UploadEntry[]>([]);

  const onFiles = async (picked: File[]) => {
    if (picked.length === 0) return;
    const started = picked.map((file) => ({
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
    }));
    setEntries((prev) => [
      ...prev,
      ...started.map(
        (s): UploadEntry => ({
          id: s.id,
          name: s.file.name,
          sizeBytes: s.file.size,
          state: "uploading",
          message: null,
        }),
      ),
    ]);
    let anySucceeded = false;
    await Promise.all(
      started.map(async (s) => {
        try {
          await leadGeneratorApi.uploadAttachment(preleadId, s.file);
          anySucceeded = true;
          setEntries((prev) =>
            prev.map((e) => (e.id === s.id ? { ...e, state: "done", message: "Attached" } : e)),
          );
        } catch (err) {
          setEntries((prev) =>
            prev.map((e) =>
              e.id === s.id
                ? { ...e, state: "error", message: err instanceof Error ? err.message : "Upload failed" }
                : e,
            ),
          );
        }
      }),
    );
    if (anySucceeded) onUploaded();
  };

  if (!open) {
    return (
      <Button
        variant="text"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={`leadgen-attach-open-${preleadId}`}
      >
        Add file
      </Button>
    );
  }

  return (
    <div className="space-y-space-2" data-testid={`leadgen-attach-${preleadId}`}>
      <Dropzone
        onFiles={(picked) => void onFiles(picked)}
        multiple
        maxSizeBytes={MAX_FILE_BYTES}
        hint="Drop files here (up to 25 MB each)"
        data-testid={`leadgen-attach-drop-${preleadId}`}
      />
      {entries.length > 0 && (
        <ul className="flex flex-col gap-space-2 list-none p-0 m-0">
          {entries.map((e) => (
            <li key={e.id}>
              <UploadFileRow
                name={e.name}
                sizeBytes={e.sizeBytes}
                state={e.state}
                message={e.message ?? undefined}
                onRemove={
                  e.state === "uploading"
                    ? undefined
                    : () => setEntries((prev) => prev.filter((x) => x.id !== e.id))
                }
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Earnings breakdown (display-only) ────────────────────────────────────────

const EARNING_STATE_LABEL: Record<string, string> = {
  Estimated: "Estimated",
  PendingReview: "Pending",
  Approved: "Approved",
  Adjusted: "Adjusted",
};

const EARNING_STATE_TONE: Record<string, StatusTone> = {
  Estimated: "neutral",
  PendingReview: "warn",
  Approved: "ok",
  Adjusted: "muted",
};

function EarningsCard({ earnings }: { earnings: WidgetData<PreleadEarningsDto> }) {
  return (
    <Card className="space-y-space-3" data-testid="leadgen-detail-earnings">
      <div className="flex items-center justify-between gap-space-3">
        <h3 className="font-display text-label uppercase tracking-label text-gray-mid m-0">
          Earnings
        </h3>
        {earnings.data && (
          <span
            className="text-small font-semibold tabular-nums text-status-success"
            data-testid="leadgen-detail-earnings-total"
          >
            {formatCurrency(earnings.data.total, MONEY)} total
          </span>
        )}
      </div>
      {earnings.status === "loading" && !earnings.data ? (
        <Skeleton className="h-16 w-full" />
      ) : earnings.status === "error" && !earnings.data ? (
        <p className="text-small text-status-error m-0" role="alert">
          {earnings.error ?? "Couldn't load earnings."}
        </p>
      ) : !earnings.data || earnings.data.lineItems.length === 0 ? (
        <p className="text-small text-gray-mid m-0">No credits yet.</p>
      ) : (
        <ul className="flex flex-col gap-space-2 list-none p-0 m-0">
          {earnings.data.lineItems.map((line, i) => (
            <li
              key={`${line.kpiCode}-${line.state}-${i}`}
              className="flex items-center justify-between gap-space-3"
            >
              <div className="flex min-w-0 items-center gap-space-2">
                <span className="text-small text-onyx truncate">{line.label}</span>
                <StatusBadge tone={EARNING_STATE_TONE[line.state] ?? "neutral"}>
                  {EARNING_STATE_LABEL[line.state] ?? line.state}
                </StatusBadge>
              </div>
              <span className="text-small font-semibold tabular-nums text-onyx">
                {formatCurrency(line.amount, MONEY)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── Notes thread + Add note + Paste update ───────────────────────────────────

function NotesCard({
  preleadId,
  notes,
  onDetailMerged,
}: {
  preleadId: string;
  notes: WidgetData<PreleadNoteDto[]>;
  onDetailMerged: (detail: PreleadDetailDto) => void;
}) {
  const [noteBody, setNoteBody] = useState("");
  const addNote = useSubmittingMutation(() => leadGeneratorApi.addNote(preleadId, noteBody.trim()), {
    onSuccess: () => {
      setNoteBody("");
      notes.reload();
    },
  });

  return (
    <Card className="space-y-space-3">
      <h3 className="font-display text-label uppercase tracking-label text-gray-mid m-0">Notes</h3>

      {notes.status === "loading" && !notes.data ? (
        <Skeleton className="h-24 w-full" />
      ) : !notes.data || notes.data.length === 0 ? (
        <p className="text-small text-gray-mid m-0">No notes yet.</p>
      ) : (
        <ul className="flex flex-col gap-space-2 list-none p-0 m-0">
          {notes.data.map((n) => (
            <li
              key={n.id}
              className="rounded-md border border-border-light bg-white-warm p-space-3 space-y-space-1"
            >
              <div className="flex items-center gap-space-2">
                <StatusBadge tone={n.source === "AI" ? "info" : "neutral"}>
                  {n.source === "AI" ? "AI" : "Note"}
                </StatusBadge>
                <Timestamp value={n.createdAtUtc} />
              </div>
              <p className="text-small text-onyx whitespace-pre-wrap m-0">{n.body}</p>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-space-2">
        <Textarea
          value={noteBody}
          onChange={(e) => setNoteBody(e.target.value)}
          rows={2}
          placeholder="Add a note…"
          aria-label="Add a note"
        />
        <div className="flex items-center justify-between gap-space-3">
          {addNote.error != null && (
            <p className="text-caption text-status-error m-0" role="alert">
              {addNote.error instanceof Error ? addNote.error.message : "Couldn't add the note."}
            </p>
          )}
          <Button
            variant="secondary"
            size="sm"
            className="ml-auto"
            onClick={() => void addNote.submit()}
            loading={addNote.pending}
            disabled={noteBody.trim().length === 0}
            data-testid={`leadgen-add-note-${preleadId}`}
          >
            Add note
          </Button>
        </div>
      </div>

      <PasteUpdateBox preleadId={preleadId} onMerged={onDetailMerged} onNoteAdded={() => notes.reload()} />
    </Card>
  );
}

function PasteUpdateBox({
  preleadId,
  onMerged,
  onNoteAdded,
}: {
  preleadId: string;
  onMerged: (detail: PreleadDetailDto) => void;
  onNoteAdded: () => void;
}) {
  const [text, setText] = useState("");
  const [discrepancies, setDiscrepancies] = useState<PreleadFieldDiscrepancyDto[]>([]);
  // Keep the last submitted text so "Apply anyway" can resubmit with confirm.
  const lastText = useRef("");

  const rePaste = useSubmittingMutation(
    (confirmOverwrite: boolean) =>
      leadGeneratorApi.rePaste(preleadId, {
        rawText: (confirmOverwrite ? lastText.current : text).trim(),
        confirmOverwrite,
      }),
    {
      onSuccess: (result) => {
        onMerged(result.prelead);
        onNoteAdded();
        const unresolved = result.discrepancies.filter((d) => !d.applied);
        setDiscrepancies(unresolved);
        if (unresolved.length === 0) setText("");
      },
    },
  );

  const submit = () => {
    lastText.current = text.trim();
    setDiscrepancies([]);
    void rePaste.submit(false);
  };

  return (
    <div className="space-y-space-2 rounded-md border border-dashed border-border-light p-space-3">
      <div className="flex items-center gap-space-2">
        <Sparkles size={14} className="text-gold-shadow" aria-hidden />
        <p className="text-small font-semibold text-onyx m-0">Paste an update</p>
      </div>
      <p className="text-caption text-gray-mid m-0">
        Paste new listing / thread text — we&apos;ll add an AI note and fill any blank fields.
      </p>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder="Paste the newer message or listing here…"
        aria-label="Paste an update"
      />

      {discrepancies.length > 0 && (
        <div
          className="space-y-space-2 rounded-md border border-status-warning/60 bg-status-warning/10 p-space-2"
          role="alert"
        >
          <p className="text-caption font-semibold text-onyx m-0">
            These fields differ from what&apos;s on file — kept the existing values:
          </p>
          <ul className="list-none p-0 m-0 space-y-space-1">
            {discrepancies.map((d) => (
              <li key={d.field} className="text-caption text-onyx">
                <span className="font-semibold">{d.field}:</span>{" "}
                <span className="text-gray-mid">{d.existingValue || "—"}</span> →{" "}
                <span>{d.pastedValue || "—"}</span>
              </li>
            ))}
          </ul>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void rePaste.submit(true)}
            loading={rePaste.pending}
            data-testid={`leadgen-repaste-apply-${preleadId}`}
          >
            Apply pasted values
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between gap-space-3">
        {rePaste.error != null && (
          <p className="text-caption text-status-error m-0" role="alert">
            {rePaste.error instanceof Error ? rePaste.error.message : "Couldn't read that paste."}
          </p>
        )}
        <Button
          variant="secondary"
          size="sm"
          className="ml-auto"
          onClick={submit}
          loading={rePaste.pending && discrepancies.length === 0}
          disabled={text.trim().length === 0}
          data-testid={`leadgen-repaste-${preleadId}`}
        >
          Paste update
        </Button>
      </div>
    </div>
  );
}

// ── Activity timeline ────────────────────────────────────────────────────────

function ActivityCard({ activity }: { activity: WidgetData<PreleadActivityDto[]> }) {
  return (
    <Card className="space-y-space-2">
      <h3 className="font-display text-label uppercase tracking-label text-gray-mid m-0">Activity</h3>
      {activity.status === "loading" && !activity.data ? (
        <Skeleton className="h-24 w-full" />
      ) : !activity.data || activity.data.length === 0 ? (
        <EmptyState title="No activity yet" description="Advance a track to start the timeline." icon={Sparkles} />
      ) : (
        <ol className="flex flex-col gap-space-2 list-none p-0 m-0">
          {activity.data.map((a) => (
            <li
              key={a.id}
              className={cn("flex items-start gap-space-3 border-l-2 border-border-light pl-space-3")}
            >
              <div className="min-w-0">
                <div className="text-small text-onyx">{a.description}</div>
                <Timestamp value={a.timestamp} />
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
